// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { FileSpreadsheet, Plus, Copy, ExternalLink, Lock, RotateCcw, Trash2, Send, Pencil, Save, Check } from 'lucide-react';
import { formatCalendarDate } from "@/lib/dates";

interface SheetToken {
  id: string;
  token: string;
  event_id: string | null;
  label: string;
  permission: 'view' | 'comment' | 'edit';
  allowed_emails: string[] | null;
  created_at: string;
  revoked: boolean;
  scope: string;
  created_by: string | null;
  show_checkin: boolean;
}

interface EventOption {
  id: string;
  name: string;
  start_date: string | null;
}

const PERMISSION_LABELS: Record<string, { label: string; color: string }> = {
  view: { label: 'View only', color: 'bg-muted text-muted-foreground' },
  comment: { label: 'Comment', color: 'bg-blue-100 text-blue-700' },
  edit: { label: 'Edit', color: 'bg-amber-100 text-amber-700' },
};

const StaffLiveSheetsPage: React.FC = () => {
  const { profile } = useAuth();
  const [tokens, setTokens] = useState<SheetToken[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Create form
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [newPermission, setNewPermission] = useState<'view' | 'comment' | 'edit'>('view');
  const [useEmailGate, setUseEmailGate] = useState(false);
  const [newEmails, setNewEmails] = useState('');

  const fetchAll = async () => {
    if (!profile?.id) return;
    const [tokenRes, eventRes] = await Promise.all([
      supabase
        .from('event_sheet_tokens')
        .select('*')
        .eq('scope', 'checkin')
        .order('created_at', { ascending: false }),
      supabase
        .from('events')
        .select('id, name, start_date')
        .order('start_date', { ascending: false })
        .limit(100),
    ]);
    setTokens((tokenRes.data as SheetToken[]) || []);
    setEvents((eventRes.data as EventOption[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [profile?.id]);

  const buildUrl = (t: SheetToken) =>
    `${window.location.origin}/checkin-sheet/${t.token}`;

  const parseEmails = (raw: string): string[] =>
    raw.split(/[\n,;]+/).map(e => e.trim().toLowerCase()).filter(e => e.includes('@'));

  const copyLink = async (t: SheetToken) => {
    await navigator.clipboard.writeText(buildUrl(t));
    setCopiedId(t.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Link copied!');
  };

  const handleCreate = async () => {
    if (!newLabel.trim()) { toast.error('Add a label'); return; }
    if (selectedEventIds.length === 0) { toast.error('Select at least one event'); return; }
    const emailList = useEmailGate ? parseEmails(newEmails) : null;
    if (useEmailGate && (!emailList || emailList.length === 0)) { toast.error('Add at least one email'); return; }

    setCreating(true);
    const createdTokens: SheetToken[] = [];

    for (const eventId of selectedEventIds) {
      const eventName = events.find(e => e.id === eventId)?.name || '';
      const label = selectedEventIds.length > 1
        ? `${newLabel.trim()} — ${eventName}`
        : newLabel.trim();

      const { data, error } = await supabase
        .from('event_sheet_tokens')
        .insert({
          event_id: eventId,
          label,
          permission: newPermission,
          allowed_emails: emailList,
          scope: 'checkin',
          created_by: profile?.id,
          show_checkin: true, // always enable check-in for staff sheets
        } as any)
        .select().single();

      if (!error && data) createdTokens.push(data as SheetToken);
    }

    setCreating(false);
    if (createdTokens.length === 0) { toast.error('Failed to create'); return; }
    setTokens(prev => [...createdTokens, ...prev]);
    resetForm();

    await navigator.clipboard.writeText(buildUrl(createdTokens[0]));

    if (emailList && emailList.length > 0) {
      for (const t of createdTokens) {
        sendSheetEmails(emailList, t.label, buildUrl(t));
      }
    }
    toast.success(`${createdTokens.length} sheet${createdTokens.length > 1 ? 's' : ''} created! First link copied.`);
  };

  const sendSheetEmails = async (emails: string[], label: string, sheetUrl: string) => {
    for (const email of emails) {
      try {
        // Insert into communication_logs so the existing email pipeline sends it
        await supabase.from('communication_logs').insert({
          type: 'email',
          recipient_email: email,
          subject: `You've been shared a Check-In Sheet: ${label}`,
          content: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <div style="background: #002E62; padding: 20px; text-align: center;">
                <h1 style="color: #fff; margin: 0; font-size: 22px;">RecruitLook</h1>
              </div>
              <div style="padding: 30px 20px;">
                <h2 style="color: #002E62; margin-top: 0;">You've been shared a Check-In Sheet</h2>
                <p style="color: #333; font-size: 15px;">You have access to <strong>${label}</strong> on RecruitLook.</p>
                <p style="text-align: center; margin: 30px 0;">
                  <a href="${sheetUrl}" style="display: inline-block; background: #002E62; color: #fff; padding: 14px 32px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px;">Open Check-In Sheet</a>
                </p>
                <p style="color: #999; font-size: 13px; margin-top: 30px;">This link was shared with you by a RecruitLook staff member.</p>
              </div>
            </div>
          `,
          status: 'pending',
          metadata: { source: 'staff_live_sheets', sheet_label: label },
        });
      } catch (err) {
        console.error('Failed to queue email for', email, err);
      }
    }
    toast.success(`Email queued for ${emails.length} recipient${emails.length > 1 ? 's' : ''}`);
  };

  const handleSendEmails = async (t: SheetToken) => {
    if (!t.allowed_emails || t.allowed_emails.length === 0) {
      toast.error('No emails assigned to this sheet');
      return;
    }
    sendSheetEmails(t.allowed_emails, t.label, buildUrl(t));
  };

  const resetForm = () => {
    setNewLabel(''); setSelectedEventIds([]); setNewPermission('view');
    setUseEmailGate(false); setNewEmails(''); setShowForm(false);
  };

  const handleRevoke = async (id: string) => {
    await supabase.from('event_sheet_tokens').update({ revoked: true, revoked_at: new Date().toISOString() }).eq('id', id);
    setTokens(prev => prev.map(t => t.id === id ? { ...t, revoked: true } : t));
    toast.success('Revoked');
  };

  const handleRestore = async (id: string) => {
    await supabase.from('event_sheet_tokens').update({ revoked: false, revoked_at: null }).eq('id', id);
    setTokens(prev => prev.map(t => t.id === id ? { ...t, revoked: false } : t));
    toast.success('Restored');
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('event_sheet_tokens').delete().eq('id', id);
    if (error) { toast.error('Failed to delete'); return; }
    setTokens(prev => prev.filter(t => t.id !== id));
    toast.success('Sheet deleted permanently');
  };

  const handleUpdateEmails = (id: string, emails: string[]) => {
    setTokens(prev => prev.map(t => t.id === id ? { ...t, allowed_emails: emails.length > 0 ? emails : null } : t));
  };

  const getEventName = (eventId: string | null) => {
    if (!eventId) return 'Unknown';
    return events.find(e => e.id === eventId)?.name || 'Unknown Event';
  };

  const activeTokens = tokens.filter(t => !t.revoked);
  const revokedTokens = tokens.filter(t => t.revoked);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-secondary" />
            Check-In Live Sheets
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create and share check-in sheets for events — viewers can see who has checked in.
          </p>
        </div>
        <Button onClick={() => setShowForm(f => !f)} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Check-In Sheet
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Create Check-In Sheet</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Event selector */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Events ({selectedEventIds.length} selected)
                </label>
                <button
                  type="button"
                  onClick={() => setSelectedEventIds(prev => prev.length === events.length ? [] : events.map(e => e.id))}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  {selectedEventIds.length === events.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto border border-border rounded-md bg-background p-2 space-y-1">
                {events.map(ev => {
                  const checked = selectedEventIds.includes(ev.id);
                  return (
                    <label key={ev.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setSelectedEventIds(prev => checked ? prev.filter(id => id !== ev.id) : [...prev, ev.id])}
                        className="rounded"
                      />
                      <span className="text-sm text-foreground">{ev.name}</span>
                      {ev.start_date && (
                        <span className="text-xs text-muted-foreground ml-auto">
                          {formatCalendarDate(ev.start_date, "M/d/yyyy")}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Label */}
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Label (who is this for?)</label>
              <input
                className="w-full text-sm border border-border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
                placeholder="e.g. Venue Staff, College Coach Check-In"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
              />
            </div>

            {/* Permission */}
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Access Level</label>
              <div className="grid grid-cols-3 gap-2">
                {(['view', 'comment', 'edit'] as const).map(p => (
                  <button
                    key={p}
                    onClick={() => setNewPermission(p)}
                    className={`text-left p-2.5 rounded-lg border-2 transition-all ${newPermission === p ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'}`}
                  >
                    <div className={`text-xs font-semibold px-1.5 py-0.5 rounded inline-block ${PERMISSION_LABELS[p].color}`}>
                      {PERMISSION_LABELS[p].label}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Email gate */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={useEmailGate} onChange={e => setUseEmailGate(e.target.checked)} className="rounded" />
                <span className="text-xs font-medium text-muted-foreground">Restrict to specific emails</span>
              </label>
              {useEmailGate && (
                <textarea
                  className="w-full mt-2 text-xs border border-border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                  rows={3}
                  placeholder="One email per line or comma-separated"
                  value={newEmails}
                  onChange={e => setNewEmails(e.target.value)}
                />
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating…' : 'Create & Copy Link'}
              </Button>
              <Button variant="ghost" onClick={resetForm}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading sheets…</div>
      ) : activeTokens.length === 0 && !showForm ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileSpreadsheet className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-muted-foreground">No check-in sheets yet. Create your first one above.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {activeTokens.map(t => (
            <StaffSheetRow
              key={t.id}
              token={t}
              copiedId={copiedId}
              onCopy={copyLink}
              onRevoke={handleRevoke}
              onDelete={handleDelete}
              onSend={handleSendEmails}
              onUpdateEmails={handleUpdateEmails}
              getEventName={getEventName}
            />
          ))}
        </div>
      )}

      {revokedTokens.length > 0 && (
        <details className="text-xs">
          <summary className="text-muted-foreground cursor-pointer hover:text-foreground select-none">
            {revokedTokens.length} revoked sheet{revokedTokens.length > 1 ? 's' : ''}
          </summary>
          <div className="mt-2 space-y-1.5">
            {revokedTokens.map(t => (
              <div key={t.id} className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded border border-border opacity-60">
                <span className="text-muted-foreground line-through flex-1">{t.label} — {getEventName(t.event_id)}</span>
                <button onClick={() => handleRestore(t.id)} className="text-xs text-primary hover:underline flex items-center gap-1">
                  <RotateCcw className="h-3 w-3" /> Restore
                </button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

// Row component for staff sheets
const StaffSheetRow: React.FC<{
  token: SheetToken;
  copiedId: string | null;
  onCopy: (t: SheetToken) => void;
  onRevoke: (id: string) => void;
  onDelete: (id: string) => void;
  onSend: (t: SheetToken) => void;
  onUpdateEmails: (id: string, emails: string[]) => void;
  getEventName: (id: string | null) => string;
}> = ({ token: t, copiedId, onCopy, onRevoke, onDelete, onSend, onUpdateEmails, getEventName }) => {
  const [editing, setEditing] = useState(false);
  const [editEmails, setEditEmails] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCheckin, setShowCheckin] = useState(t.show_checkin ?? true);

  const url = `${window.location.origin}/checkin-sheet/${t.token}`;
  const perm = PERMISSION_LABELS[t.permission] || PERMISSION_LABELS.view;

  const startEdit = () => {
    setEditEmails((t.allowed_emails || []).join('\n'));
    setEditing(true);
  };

  const saveEmails = async () => {
    setSaving(true);
    const parsed = editEmails.split(/[\n,;]+/).map(e => e.trim().toLowerCase()).filter(e => e.includes('@'));
    const { error } = await supabase
      .from('event_sheet_tokens')
      .update({ allowed_emails: parsed.length > 0 ? parsed : null })
      .eq('id', t.id);
    setSaving(false);
    if (error) { toast.error('Failed to save'); return; }
    onUpdateEmails(t.id, parsed);
    setEditing(false);
    toast.success(`Updated — ${parsed.length} email${parsed.length !== 1 ? 's' : ''} assigned`);
  };

  const handleToggleCheckin = async (checked: boolean) => {
    setShowCheckin(checked);
    await supabase.from('event_sheet_tokens').update({ show_checkin: checked } as any).eq('id', t.id);
    toast.success(checked ? 'Check-in column visible on sheet' : 'Check-in column hidden');
  };

  return (
    <div className="border border-border rounded-lg bg-card">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground">{t.label}</span>
            <Badge variant="outline" className="text-[10px]">{getEventName(t.event_id)}</Badge>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${perm.color}`}>{perm.label}</span>
            {t.allowed_emails && t.allowed_emails.length > 0 && (
              <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded flex items-center gap-0.5">
                <Lock className="h-2.5 w-2.5" /> {t.allowed_emails.length}
              </span>
            )}
            <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer ml-2">
              <input type="checkbox" checked={showCheckin} onChange={e => handleToggleCheckin(e.target.checked)} className="rounded h-3 w-3 accent-green-600" />
              Show Check-In
            </label>
          </div>
          <div className="text-xs text-muted-foreground truncate mt-0.5">{url}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={startEdit}>
            <Pencil className="h-3 w-3" /> Emails
          </Button>
          {t.allowed_emails && t.allowed_emails.length > 0 && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onSend(t)}>
              <Send className="h-3 w-3" /> Send
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onCopy(t)}>
            <Copy className="h-3 w-3" /> {copiedId === t.id ? 'Copied!' : 'Copy'}
          </Button>
          <a href={url} target="_blank" rel="noreferrer">
            <Button size="sm" className="h-7 text-xs gap-1">
              <ExternalLink className="h-3 w-3" /> Open
            </Button>
          </a>
          <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => onRevoke(t.id)}>
            Revoke
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive/60 hover:text-destructive" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {editing && (
        <div className="px-4 pb-3 border-t border-border pt-3">
          <label className="text-xs font-medium text-muted-foreground block mb-1">Assigned Emails</label>
          <textarea
            className="w-full text-xs border border-border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            rows={3}
            value={editEmails}
            onChange={e => setEditEmails(e.target.value)}
            placeholder="One email per line or comma-separated"
          />
          <div className="flex gap-2 mt-2">
            <Button size="sm" className="h-7 text-xs gap-1" onClick={saveEmails} disabled={saving}>
              <Save className="h-3 w-3" /> {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this sheet permanently?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone. The shared link will stop working immediately.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { onDelete(t.id); setShowDeleteConfirm(false); }}>
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffLiveSheetsPage;
